import {useCallback, useContext, useEffect, useState} from 'react';
import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import classNames from 'classnames';

import {getRecipientFromThread} from 'shells/inbox/helpers';
import {Stack} from 'shells/layout/stack/stack';
import {LoadingPage} from 'shells/loading-page/loading-page';

import type {ThreadType} from 'nutshell-graphql-types';
import type {InboxThreadFragment} from 'nutshell-graphql-types';

import {readLocalStorage, writeLocalStorage} from '../../../util/localStorage';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {RouteContext} from '../../route-context';
import {useGetInboxStatus} from '../../setup/text-messaging/graphql/hooks/use-get-inbox-status';

import {useGetChannelEvents, useMarkThreadAsRead} from './../graphql/hooks';
import {getThreadStatusFromSelectedTab, getCantSendStatusEnum} from './../helpers';
import {InboxThreadHeader} from './inbox-thread-header';
import {LeadSidebar} from './lead-sidebar/lead-sidebar';
import {ThreadContent} from './thread-content';
import {ThreadContentEmptyState} from './thread-content-empty-state';
import {ThreadList} from './thread-list';
import {ThreadListHeader} from './thread-list-header';

import styles from './inbox-page.module.css';

const INBOX_LEAD_SIDEBAR_OPEN_KEY = 'inbox_lead_sidebar_open';

type Props = ContextRouter & {
    selectedView: string | null | undefined;
    latestThreads: InboxThreadFragment[];
    latestThreadsTotalCount: number;
    isLoadingLatestThreads: boolean;
    fetchMoreThreads: () => Promise<any>;
    hasMoreThreads: boolean;
    refetchThreads?: () => Promise<any>;
    assigneeIds: string[];
    threadType: ThreadType | null | undefined;
    search?: string;
    onSearchChange?: (value: string) => void;
};

export function InboxPageContent(props: Props) {
    const {
        selectedView,
        latestThreads,
        latestThreadsTotalCount,
        isLoadingLatestThreads,
        fetchMoreThreads,
        hasMoreThreads,
        refetchThreads,
        threadType,
    } = props;
    const selectedThreadId = props.match.params.id;

    const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
    const [selectedThread, setSelectedThread] = useState<InboxThreadFragment | null | undefined>(
        null
    );
    const [isViewingNewThread, setIsViewingNewThread] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    // Starts as undefined, will be set to a status or null once the function runs
    const [cantSendStatus, setCantSendStatus] = useState<any>(undefined);
    const [isLeadSidebarOpen, setIsLeadSidebarOpen] = useState<boolean>(() => {
        const stored = readLocalStorage(INBOX_LEAD_SIDEBAR_OPEN_KEY);
        return stored != null ? stored === 'true' : false;
    });

    const setIsLeadSidebarOpenPersisted = useCallback((open: boolean) => {
        setIsLeadSidebarOpen(open);
        writeLocalStorage(INBOX_LEAD_SIDEBAR_OPEN_KEY, String(open));
    }, []);

    const handleSearchToggle = useCallback(() => {
        setIsSearchOpen((prev) => {
            if (prev && props.onSearchChange) {
                props.onSearchChange('');
            }
            return !prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.onSearchChange]);

    const {inboxStatus} = useGetInboxStatus();
    const {user} = useGetSessionUser();
    const {isEmbedded} = useContext(RouteContext);
    const shouldShowLeadSidebar = NutClientConfig.hasLeadSidebarFF && !isEmbedded;

    const {
        sendabilityStatus,
        channelEvents,
        isLoading: isLoadingChannelEvents,
        fetchMostRecentChannelEvents,
        refetchScheduledChannelEvents,
        fetchMoreRows,
        hasNextPage,
        lastSeenTime,
        isActive,
    } = useGetChannelEvents(selectedChannelId, selectedThread ? selectedThread.id : null);
    const {markThreadAsRead: markThreadAsReadMutation} = useMarkThreadAsRead();

    const markThreadAsRead = useCallback(
        (threadId: string) => {
            markThreadAsReadMutation(threadId);
        },
        [markThreadAsReadMutation]
    );

    const channelStatus = selectedThread && selectedThread.externalChannel.status;

    const setSelectedThreadFromUrl = useCallback(
        (threadId: string) => {
            const newPath = generatePath(props.match.path, {
                section: props.match.params.section,
                page: props.match.params.page,
                id: threadId,
            });

            props.history.push({
                pathname: newPath,
                search: props.history.location.search,
            });
        },
        [props.match.path, props.match.params, props.history]
    );

    // We don't display previously loaded channel events when a new thread is selected and those channel events are being fetched
    useEffect(() => {
        if (isViewingNewThread && !isLoadingChannelEvents && channelEvents.length > 0) {
            setIsViewingNewThread(false);
        }
    }, [isViewingNewThread, isLoadingChannelEvents, channelEvents]);

    // Clear selected thread/channel state when the view or search changes
    useEffect(() => {
        setSelectedThread(null);
        setSelectedChannelId(null);
    }, [selectedView, props.search]);

    // Auto-select either the thread from the url or the first thread when the list loads. Loads with a tiny delay to ensure the previous state is cleared
    useEffect(() => {
        if (latestThreads.length > 0 && !isLoadingLatestThreads && !selectedChannelId) {
            let threadToSelect;
            const threadFound = latestThreads.find(
                // @ts-expect-error
                (latestThread) => latestThread.id === selectedThreadId
            );
            if (threadFound) {
                threadToSelect = threadFound;
                setSelectedThread(threadToSelect);
            } else {
                // this is a fallback for when the thread is not found in the latestThreads array, shouldn't happen really
                threadToSelect = latestThreads[0];
                setSelectedThread(threadToSelect);
                setSelectedThreadFromUrl(threadToSelect.id);
            }
            setSelectedChannelId(threadToSelect.externalChannel.id);

            // Scroll the selected thread into view
            const threadElement = document.getElementById(threadToSelect.id);
            if (threadElement) {
                threadElement.scrollIntoView({behavior: 'smooth'});
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestThreads, isLoadingLatestThreads, selectedChannelId, selectedThreadId]);

    useEffect(() => {
        // Keep the selected channel in sync with the selected thread
        if (selectedThread) {
            setSelectedChannelId(selectedThread.externalChannel.id);
        } else {
            setSelectedChannelId(null);
        }

        // If the selected thread is updated, update the selected thread state to the new version
        if (selectedThread && latestThreads) {
            // @ts-expect-error
            const updatedThread = latestThreads.find((thread) => thread.id === selectedThread.id);
            setSelectedThread(updatedThread);
        }
    }, [latestThreads, selectedThread]);

    useEffect(() => {
        if (selectedThread && selectedThread.isUnread) {
            markThreadAsRead(selectedThread.id);
        }
    }, [selectedThread, markThreadAsRead]);

    const externalPhone = selectedThread && selectedThread.externalChannel.phone;
    const recipient = selectedThread ? getRecipientFromThread(selectedThread) : null;

    const shouldShowThread = selectedThread && selectedChannelId && recipient;
    const shouldShowLoadingState = !shouldShowThread && isLoadingLatestThreads;
    const noThreadsEmptyState =
        !shouldShowThread && !isLoadingLatestThreads && !latestThreads.length;

    const selectedThreadType: ThreadType | null | undefined =
        selectedThread && selectedThread.threadType;

    useEffect(() => {
        if (user && inboxStatus && selectedThread && selectedThreadType === 'SMS') {
            const statusEnum = getCantSendStatusEnum({
                isInboxPage: true,
                userCanUseSms: user.permissions.canUseSms,
                inboxStatus,
                channelStatus,
                selectedThread,
                hasSmsCampaigns:
                    NutClientConfig.hasSmsCampaignsFeature && user.permissions.canAccessMarketing,
            });
            setCantSendStatus(statusEnum);
        } else {
            setCantSendStatus(sendabilityStatus);
        }
    }, [user, inboxStatus, selectedThread, channelStatus, selectedThreadType, sendabilityStatus]);

    const threadContainerClassName = classNames(styles['thread-container'], {
        [styles['thread-container-sidebar-collapsed']]: shouldShowLeadSidebar && !isLeadSidebarOpen,
        [styles['thread-container-sidebar-open']]: shouldShowLeadSidebar && isLeadSidebarOpen,
    });

    return (
        <Stack isFullWidth={true}>
            <div className={styles['top-row']}>
                <div className={styles['top-row-left']}>
                    <ThreadListHeader
                        {...props}
                        latestThreads={latestThreads}
                        threadCount={latestThreadsTotalCount}
                        selectedThread={selectedThread}
                        isLoadingLatestThreads={isLoadingLatestThreads}
                        onSearchToggle={props.onSearchChange ? handleSearchToggle : undefined}
                        isSearchActive={isSearchOpen}
                    />
                </div>
                <div className={styles['top-row-right']}>
                    <InboxThreadHeader
                        thread={selectedThread}
                        // @ts-expect-error
                        recipient={
                            recipient
                                ? {
                                      ...recipient,
                                      lastSeenTime: lastSeenTime || '',
                                      isActive: isActive,
                                  }
                                : null
                        }
                        threadType={selectedThreadType}
                        refetchThreads={refetchThreads}
                        fetchMostRecentChannelEvents={fetchMostRecentChannelEvents}
                        isLoadingChannelEvents={isLoadingChannelEvents}
                    />
                </div>
            </div>
            <div className={styles['content-container']}>
                <ThreadList
                    {...props}
                    isLoading={isLoadingLatestThreads}
                    selectedThread={selectedThread}
                    setSelectedThread={(thread: InboxThreadFragment) => {
                        setIsViewingNewThread(true);
                        setSelectedThread(thread);
                        setSelectedThreadFromUrl(thread.id);
                    }}
                    fetchMoreRows={fetchMoreThreads}
                    hasNextPage={hasMoreThreads}
                    isSearchOpen={isSearchOpen}
                />
                <div className={threadContainerClassName}>
                    {shouldShowLoadingState && <LoadingPage />}
                    {shouldShowThread &&
                        // the below are just duplicate checks to appease flow
                        selectedThread &&
                        selectedChannelId &&
                        recipient && (
                            <ThreadContent
                                // @ts-expect-error
                                recipient={{
                                    ...recipient,
                                    lastSeenTime: lastSeenTime || '',
                                    isActive: isActive,
                                }}
                                channelEvents={isViewingNewThread ? [] : channelEvents}
                                isLoadingChannelEvents={isLoadingChannelEvents}
                                thread={selectedThread}
                                channelId={selectedChannelId}
                                fetchMoreRows={fetchMoreRows}
                                hasNextPage={hasNextPage}
                                // @ts-expect-error
                                externalNumber={externalPhone ? externalPhone.id : ''}
                                cantSendStatus={cantSendStatus}
                                fetchMostRecentChannelEvents={fetchMostRecentChannelEvents}
                                refetchScheduledChannelEvents={refetchScheduledChannelEvents}
                                threadType={selectedThreadType}
                                refetchThreads={refetchThreads}
                            />
                        )}
                    {noThreadsEmptyState && (
                        <ThreadContentEmptyState
                            canAccessSetup={Boolean(user && user.permissions.canAccessSetup)}
                            status={getThreadStatusFromSelectedTab(selectedView)}
                            threadType={threadType}
                            selectedTab={selectedView}
                        />
                    )}
                </div>
                {shouldShowLeadSidebar && (
                    <LeadSidebar
                        isOpen={isLeadSidebarOpen}
                        setIsOpen={setIsLeadSidebarOpenPersisted}
                        isLoadingRelatedLeads={isLoadingLatestThreads}
                        thread={selectedThread ?? undefined}
                    />
                )}
            </div>
        </Stack>
    );
}
